import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle2, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import SEO from '../components/SEO';
import { useLang } from '../context/LanguageContext';
import { COMPANY } from '../mock';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  firstName: '', lastName: '', company: '', email: '', phone: '',
  origin: '', destination: '', cargo: '', weight: '', date: '', message: '',
};

const Contact = () => {
  const { t, lang } = useLang();
  const { toast } = useToast();
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/inquiries`, { ...form, lang });
      if (res.data && res.data.ok) {
        setSubmitted(true);
        toast({
          title: t.contact.form.successTitle,
          description: t.contact.form.successMsg,
        });
        setForm(emptyForm);
        setTimeout(() => setSubmitted(false), 8000);
      } else {
        throw new Error('bad_response');
      }
    } catch (err) {
      toast({
        title: t.contact.form.errorTitle,
        description: t.contact.form.errorMsg,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const contactCards = [
    { icon: Mail, label: t.contact.info.emailLabel, value: COMPANY.email, href: `mailto:${COMPANY.email}` },
    { icon: Phone, label: t.contact.info.phoneLabel, value: COMPANY.phone, href: `tel:${COMPANY.phoneRaw}` },
    { icon: MapPin, label: t.contact.info.addressLabel, value: COMPANY.address },
    { icon: Clock, label: t.contact.info.hoursLabel, value: t.contact.info.hoursValue },
  ];

  return (
    <div>
      <SEO page="contact" />
      {/* Header */}
      <section className="relative bg-gradient-to-b from-slate-50 to-white pt-16 md:pt-24 pb-10 md:pb-14">
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <div className="inline-flex items-center gap-2 text-[11.5px] font-semibold uppercase tracking-[0.18em] text-blue-800">
            <span className="w-6 h-px bg-red-500" />{t.contact.eyebrow}<span className="w-6 h-px bg-red-500" />
          </div>
          <h1 className="mt-5 text-[40px] md:text-[54px] leading-[1.05] font-semibold tracking-tight text-slate-900">{t.contact.title}</h1>
          <p className="mt-5 text-[16.5px] text-slate-600 max-w-2xl mx-auto leading-relaxed">{t.contact.subtitle}</p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10">
          {/* Form */}
          <div className="lg:col-span-8">
            <form onSubmit={onSubmit} className="bg-white rounded-3xl border border-slate-200 p-6 md:p-9 shadow-[0_30px_60px_-30px_rgba(15,23,42,0.15)]">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="firstName" className="text-[13px] font-medium text-slate-700">{t.contact.form.firstName} <span className="text-blue-700">*</span></Label>
                  <Input id="firstName" required value={form.firstName} onChange={set('firstName')} className="mt-1.5 h-11 border-slate-200 focus-visible:ring-blue-700/30 focus-visible:border-blue-700" />
                </div>
                <div>
                  <Label htmlFor="lastName" className="text-[13px] font-medium text-slate-700">{t.contact.form.lastName} <span className="text-blue-700">*</span></Label>
                  <Input id="lastName" required value={form.lastName} onChange={set('lastName')} className="mt-1.5 h-11 border-slate-200 focus-visible:ring-blue-700/30 focus-visible:border-blue-700" />
                </div>
                <div>
                  <Label htmlFor="company" className="text-[13px] font-medium text-slate-700">{t.contact.form.company}</Label>
                  <Input id="company" value={form.company} onChange={set('company')} className="mt-1.5 h-11 border-slate-200 focus-visible:ring-blue-700/30 focus-visible:border-blue-700" />
                </div>
                <div>
                  <Label htmlFor="email" className="text-[13px] font-medium text-slate-700">{t.contact.form.email} <span className="text-blue-700">*</span></Label>
                  <Input id="email" type="email" required value={form.email} onChange={set('email')} className="mt-1.5 h-11 border-slate-200 focus-visible:ring-blue-700/30 focus-visible:border-blue-700" />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-[13px] font-medium text-slate-700">{t.contact.form.phone}</Label>
                  <Input id="phone" value={form.phone} onChange={set('phone')} className="mt-1.5 h-11 border-slate-200 focus-visible:ring-blue-700/30 focus-visible:border-blue-700" />
                </div>
                <div>
                  <Label htmlFor="date" className="text-[13px] font-medium text-slate-700">{t.contact.form.date}</Label>
                  <Input id="date" type="date" value={form.date} onChange={set('date')} className="mt-1.5 h-11 border-slate-200 focus-visible:ring-blue-700/30 focus-visible:border-blue-700" />
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-100">
                <div className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-4">
                  {lang === 'cs' ? 'Parametry přepravy' : 'Shipment details'}
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="origin" className="text-[13px] font-medium text-slate-700">{t.contact.form.origin}</Label>
                    <Input id="origin" placeholder={lang === 'cs' ? 'Např. Plzeň, CZ' : 'e.g. Prague, CZ'} value={form.origin} onChange={set('origin')} className="mt-1.5 h-11 border-slate-200 focus-visible:ring-blue-700/30 focus-visible:border-blue-700" />
                  </div>
                  <div>
                    <Label htmlFor="destination" className="text-[13px] font-medium text-slate-700">{t.contact.form.destination}</Label>
                    <Input id="destination" placeholder={lang === 'cs' ? 'Např. Stuttgart, DE' : 'e.g. Munich, DE'} value={form.destination} onChange={set('destination')} className="mt-1.5 h-11 border-slate-200 focus-visible:ring-blue-700/30 focus-visible:border-blue-700" />
                  </div>
                  <div>
                    <Label htmlFor="cargo" className="text-[13px] font-medium text-slate-700">{t.contact.form.cargo}</Label>
                    <Input id="cargo" placeholder={lang === 'cs' ? 'Palety, díly, elektronika…' : 'Pallets, parts, electronics…'} value={form.cargo} onChange={set('cargo')} className="mt-1.5 h-11 border-slate-200 focus-visible:ring-blue-700/30 focus-visible:border-blue-700" />
                  </div>
                  <div>
                    <Label htmlFor="weight" className="text-[13px] font-medium text-slate-700">{t.contact.form.weight}</Label>
                    <Input id="weight" placeholder={lang === 'cs' ? 'Např. 8× EUR palety, 2000 kg' : 'e.g. 8× EUR pallets, 2000 kg'} value={form.weight} onChange={set('weight')} className="mt-1.5 h-11 border-slate-200 focus-visible:ring-blue-700/30 focus-visible:border-blue-700" />
                  </div>
                </div>
                <div className="mt-5">
                  <Label htmlFor="message" className="text-[13px] font-medium text-slate-700">{t.contact.form.message}</Label>
                  <Textarea id="message" rows={4} value={form.message} onChange={set('message')} className="mt-1.5 border-slate-200 focus-visible:ring-blue-700/30 focus-visible:border-blue-700" />
                </div>
              </div>

              <div className="mt-7 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-[12.5px] text-slate-500">
                  <ShieldCheck size={15} className="text-emerald-500" />
                  <span>{lang === 'cs' ? 'Vaše údaje jsou v souladu s GDPR.' : 'Your data is handled in line with GDPR.'}</span>
                </div>
                <Button type="submit" disabled={submitting || submitted} className="bg-blue-700 hover:bg-blue-800 disabled:bg-blue-500 text-white rounded-full px-6 h-12 text-[15px] font-medium shadow-[0_10px_28px_-12px_rgba(29,78,216,0.55)]">
                  {submitted ? (
                    <><CheckCircle2 size={17} className="mr-1.5" /> {t.contact.form.successTitle}</>
                  ) : submitting ? (
                    <>{t.contact.form.sending}</>
                  ) : (
                    <><Send size={16} className="mr-1.5" /> {t.contact.form.send}</>
                  )}
                </Button>
              </div>
            </form>
          </div>

          {/* Info cards */}
          <div className="lg:col-span-4 space-y-4">
            {contactCards.map((c, i) => {
              const Icon = c.icon;
              const inner = (
                <div className="bg-white rounded-2xl border border-slate-200 p-5 hover:border-slate-300 transition-colors">
                  <div className="flex items-start gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center shrink-0">
                      <Icon size={18} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[11.5px] uppercase tracking-[0.14em] text-slate-500 font-semibold">{c.label}</div>
                      <div className="mt-1 text-[14.5px] text-slate-900 font-medium break-words">{c.value}</div>
                    </div>
                  </div>
                </div>
              );
              return c.href ? (
                <a key={i} href={c.href} className="block">{inner}</a>
              ) : (
                <div key={i}>{inner}</div>
              );
            })}

            <div className="bg-[#0B1D4F] text-white rounded-2xl p-6 mt-2">
              <div className="text-[11.5px] uppercase tracking-[0.14em] text-blue-500 font-semibold">{lang === 'cs' ? 'Tendry a rámcové smlouvy' : 'Tenders & framework contracts'}</div>
              <p className="mt-3 text-[14px] text-slate-300 leading-relaxed">
                {lang === 'cs'
                  ? 'Účastníme se výběrových řízení pro výrobní a logistické společnosti. Kompletní dokumentaci k prověření (pojistky, ISO, ADR) zašleme obratem.'
                  : 'We take part in tenders for manufacturers and 3PLs. Full due-diligence pack (insurance, ISO, ADR) available on request.'}
              </p>
              <a href={`mailto:${COMPANY.email}?subject=Tender%20documentation%20request`} className="mt-4 inline-flex items-center gap-1.5 text-[13.5px] font-medium text-blue-500 hover:text-blue-300 transition-colors">
                {lang === 'cs' ? 'Vyžádat dokumentaci' : 'Request documentation'} →
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
